import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { Article as Props } from '@/types';
import Fields, { resolveFields } from '@/components/frontend/Fields';
import ContentSection from '@/components/frontend/ContentSection';
import ArticleLayout, {
  FieldsLayout,
} from '@/components/frontend/ArticleLayout';
import Hero, { HeroContent } from '@/components/frontend/Hero';
import { splitFields } from './Page';
import Section, { sectionPadding } from '@/components/frontend/FullSection';

const SocialShare = dynamic(() => import('@/components/frontend/SocialShare'), {
  ssr: false,
});

const Author = dynamic(() => import('@/components/frontend/Author'));

const Article = ({
  fields,
  post_title: title,
  post_excerpt: leadText,
  featured_image,
  post_date,
  isDocumentLike = false,
  author,
}: Props & { isDocumentLike?: boolean }) => {
  const { t } = useTranslation('frontoffice');
  const [hero, contentFields] = resolveFields(fields);
  const { basicFields, sectionFields } = splitFields(contentFields);

  const { image, ...heroProps } = hero
    ? {
        title: hero.heading || title,
        leadText: hero.text || leadText,
        image: hero.image || featured_image,
        date: post_date,
      }
    : { title, leadText, image: featured_image, date: post_date };

  const content = (
    <>
      {basicFields.length ? (
        <>
          <ContentSection mb={author ? 0 : sectionPadding}>
            <HeroContent
              {...heroProps}
              author={author}
              borderTopRadius="lg"
              px={['1rem', null, 10]}
              background={isDocumentLike ? 'none' : 'white'}
              data-padded-content
            />
            <Fields fields={basicFields} />
            {isDocumentLike ? null : <SocialShare title={title} />}
          </ContentSection>
          {author ? (
            <Section
              title={t('written_by', 'Kirjoittanut')}
              as="aside"
              mb={sectionPadding}
            >
              <Author {...author} />
            </Section>
          ) : null}
        </>
      ) : (
        <HeroContent {...heroProps} pb="0" />
      )}
      {sectionFields.length && !isDocumentLike ? (
        <FieldsLayout
          bg="backgroundColor"
          mb="0 !important"
          layerStyle="fullWidth"
          py={sectionPadding}
        >
          <Fields fields={sectionFields} />
        </FieldsLayout>
      ) : null}
    </>
  );

  // Flex trickery ahead is to ensure page looks nice even with very little content
  if (!image || isDocumentLike) {
    return (
      <FieldsLayout
        as="article"
        bg={isDocumentLike ? 'none' : 'white'}
        flex="1 1 auto"
        alignItems="flex-start"
      >
        {content}
      </FieldsLayout>
    );
  }
  return (
    <ArticleLayout bg="white" flex="1 1 auto">
      <Hero image={image} />
      <FieldsLayout>{content}</FieldsLayout>
    </ArticleLayout>
  );
};

export default Article;
